import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  timestamp?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

class ApiService {
  private axiosInstance: AxiosInstance;

  constructor() {
    // Check for existing token on initialization
    const existingToken = localStorage.getItem('accessToken') || localStorage.getItem('token');
    if (existingToken && !existingToken.includes('undefined')) {
      console.log('🔑 Found existing token on init:', existingToken.substring(0, 20) + '...');
    }

    this.axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5001/api/v1',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Get token from localStorage
        const token = this.getToken();
        console.log('🔐 API Request:', {
          url: config.url,
          method: config.method,
          hasToken: !!token,
          tokenPreview: token ? token.substring(0, 20) + '...' : null
        });
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error: AxiosError) => {
        // Handle common errors
        if (error.response) {
          switch (error.response.status) {
            case 401:
              // Unauthorized - redirect to login
              this.handleUnauthorized();
              break;
            case 403:
              // Forbidden
              console.error('Access denied');
              break;
            case 404:
              // Not found
              console.error('Resource not found');
              break;
            case 500:
              // Server error
              console.error('Server error');
              break;
          }
        } else if (error.request) {
          // Network error
          console.error('Network error');
        }

        return Promise.reject(error);
      }
    );
  }

  // Token management
  private getToken(): string | null {
    // Try both possible token keys
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    if (token && token !== 'undefined' && token !== 'null') {
      return token;
    }
    return null;
  }

  setToken(token: string): void {
    localStorage.setItem('accessToken', token);
  }

  setRefreshToken(token: string): void {
    localStorage.setItem('refreshToken', token);
  }

  removeTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('mockUser');
    sessionStorage.removeItem('mockUser');
  }

  private handleUnauthorized(): void {
    console.error('=== UNAUTHORIZED ERROR ===');
    console.error('Current path:', window.location.pathname);
    console.error('Access token:', localStorage.getItem('accessToken'));
    console.error('Refresh token:', localStorage.getItem('refreshToken'));
    console.error('Mock user (localStorage):', localStorage.getItem('mockUser'));
    console.error('Mock user (sessionStorage):', sessionStorage.getItem('mockUser'));

    // Clear tokens and redirect to login
    this.removeTokens();

    // Check if we're not already on the login page to avoid redirect loop
    if (!window.location.pathname.includes('/login')) {
      console.error('Redirecting to login in 5 seconds...');
      setTimeout(() => {
        window.location.href = '/login';
      }, 5000);
    }
  }

  // HTTP methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.get<ApiResponse<T>>(url, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.post<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.put<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.patch<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.delete<ApiResponse<T>>(url, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  // File upload
  async upload<T = any>(url: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await this.axiosInstance.post<ApiResponse<T>>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  // Error handling
  private handleError(error: AxiosError): Error {
    if (error.response?.data) {
      const apiError = error.response.data as ApiResponse;
      return new Error(apiError.message || apiError.error || 'Bir hata oluştu');
    }
    if (error.message) {
      return new Error(error.message);
    }
    return new Error('Beklenmeyen bir hata oluştu');
  }

  // Get axios instance for custom configurations
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;